import { useEffect, useState } from "react";
import { Activity, AlertTriangle, DollarSign, Loader2, ShieldAlert, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { api, errMsg } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="panel p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
  </div>
);

const DailyBars = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.ok + d.failed));
  return (
    <div className="panel p-6" data-testid="admin-daily-chart">
      <h2 className="text-lg font-semibold mb-4">AI requests per day</h2>
      {daily.length === 0 ? <p className="text-sm text-muted-foreground">No requests yet.</p> : (
        <div className="flex items-end gap-2 h-40">
          {daily.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.ok} ok, ${d.failed} failed`}>
              <div className="w-full flex flex-col justify-end h-32">
                <div className="bg-rose-500 rounded-t" style={{ height: `${(d.failed / max) * 100}%` }} />
                <div className="brand-gradient rounded-b" style={{ height: `${(d.ok / max) * 100}%` }} />
              </div>
              <span className="text-[10px] text-muted-foreground">{d.day.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api.get("/admin/stats", { params: { days } }).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));
  }, [days]);

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Usage dashboard</h1>
          <div className="flex gap-1 rounded-full border border-border p-1">
            {[1, 7, 14, 30].map((d) => (
              <button key={d} data-testid={`admin-range-${d}`} onClick={() => setDays(d)} className={`h-8 px-3 rounded-full text-xs font-semibold ${days === d ? "brand-gradient text-white" : "text-muted-foreground"}`}>{d}d</button>
            ))}
          </div>
        </div>
        {error ? (
          <div data-testid="admin-error" className="panel p-6 flex gap-2 text-sm"><ShieldAlert className="h-4 w-4 text-rose-500" />{error}</div>
        ) : !data ? (
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Activity} label="AI requests" value={data.total_ai_requests} testId="admin-total-requests" />
              <Stat icon={AlertTriangle} label="Failed" value={data.failed_ai_requests} testId="admin-failed-requests" />
              <Stat icon={DollarSign} label="Est. cost (USD)" value={`$${data.estimated_cost_usd.toFixed(2)}`} testId="admin-est-cost" />
              <Stat icon={Users} label="Users" value={data.total_users} testId="admin-total-users" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Stat icon={ShieldAlert} label="Auth failures" value={data.auth_failures} testId="admin-auth-failures" />
              <Stat icon={Users} label={`At daily limit (${data.daily_limit}) today`} value={data.users_at_daily_limit_today} testId="admin-limit-hits" />
            </div>
            <DailyBars daily={data.daily} />
            <div className="panel p-6 overflow-x-auto" data-testid="admin-breakdown">
              <h2 className="text-lg font-semibold mb-4">Breakdown</h2>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground text-left"><tr><th className="py-2">Type</th><th>Status</th><th>Count</th><th>Avg ms</th><th>p95 ms</th><th>Est. cost</th></tr></thead>
                <tbody>
                  {data.breakdown.map((r) => (
                    <tr key={`${r.kind}-${r.status}`} className="border-t border-border"><td className="py-2 font-medium">{r.kind}</td><td>{r.status}</td><td>{r.count}</td><td>{r.avg_ms ? Math.round(r.avg_ms) : "—"}</td><td>{r.p95_ms ? Math.round(r.p95_ms) : "—"}</td><td>${(r.cost_usd || 0).toFixed(3)}</td></tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-4">Costs are estimates using per-request assumptions (USD): {Object.entries(data.cost_assumptions_usd).map(([k, v]) => `${k} ${v}`).join(", ")}. Check your provider billing for actual spend.</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
